import { Layout, Button } from 'antd'
import { DownOutlined, StarFilled, UpOutlined } from '@ant-design/icons'
import type { CSSProperties } from 'react'
import { useState, useEffect } from 'react'
import { colors } from '@/pages/static'
import './index.scss'
import type { StockPool, Stock } from '@/types'
import { useFeedback } from '@/utils/antdApp'
import { getPoolDetail } from '@/api'

function stockDisplayName(stock: Stock): string {
    if (stock.market === 'CN') return stock.sc_name || stock.name
    if (stock.market === 'HK') return stock.tc_name || stock.name
    return stock.name
}

const { Sider, Content } = Layout

interface StockPoolItem extends StockPool {
    open: boolean
}

export default function PoolLayout({
    poolId,
    stockCode,
    poolList,
    children,
    onSelectStockNewKB,
    onSelectPool,
    createPool,
}: {
    poolId: number,
    stockCode: string,
    poolList: StockPoolItem[],
    children: React.ReactNode,
    onSelectStockNewKB: (stock: Stock) => void,
    onSelectPool: (pool: StockPool, idx: number) => void   //pool: 股票池, idx: 股票池索引(颜色)
    createPool: () => void
}) {
    const { message } = useFeedback()
    const [activeKey, setActiveKey] = useState('1')
    const [activeStockCode, setActiveStockCode] = useState('')
    const [poolsList, setPoolsList] = useState<StockPoolItem[]>([])

    useEffect(() => {
        setActiveStockCode(stockCode)
    }, [stockCode])

    useEffect(() => {
        setPoolsList([...poolList])
    }, [poolList])

    useEffect(() => {
        if(poolId) {
            loadPoolStocks(poolId)
            setPoolsList((prev) => prev.map((item) => ({ ...item, open: item.id === poolId ? true : item.open })))
        }
    }, [poolId])

    const loadPoolStocks = (id: number) => {
        getPoolDetail(id).then((data) => {
            const stocks = Array.isArray(data?.stocks) ? data.stocks : []
            setPoolsList((prev) => prev.map((item) => ({
                ...item,
                stocks: item.id === id ? stocks : (Array.isArray(item.stocks) ? item.stocks : []),
            })))
        })
        .catch(() => message.error('加载失败'))
    }

    const changeTab = (key: string) => {
        setActiveKey(key)
        if(key === '2') {
            setPoolsList(poolList.filter((m: StockPool) => m.is_favorite).map(m => ({ ...m, open: false })))
        } else {
            setPoolsList(poolList.map((m: StockPoolItem) => ({ ...m, open: false })))
        }
    }

    const openPool = (pool: StockPoolItem) => {
        if(!pool.open){
            loadPoolStocks(pool.id)
        }
        setPoolsList((prev) => prev.map((item) => ({ ...item, open: item.id === pool.id ? !item.open : item.open })))
    }

    const selectStock = (stock: Stock) => {
        setActiveStockCode(stock.code)
        onSelectStockNewKB(stock)
    }
    
    const selectPool = (pool: StockPoolItem, idx: number) => {
        onSelectPool(pool, idx)
    }

    return (
        <Layout className='pool-layout'>
            <Sider className='pool-layout-sider'>
                <div className='pool-layout-sider-header-content'>
                    <div className='pool-layout-sider-header color-3'>
                        <span className='pool-layout-sider-header-title text-11'>股票池</span>
                        <Button size='small' className='text-10 pool-layout-sider-header-button' onClick={() => createPool()}>+&nbsp;&nbsp;新建</Button>
                    </div>
                    <div className='pool-layout-sider-content'>
                        <div className='pool-layout-sider-content-tabs color-3 text-11 flex gap-2'>
                            <span className={`pool-layout-sider-content-tabs-item ${activeKey === '1' ? 'active' : ''}`} onClick={() => changeTab('1')}>全部</span>
                            <span className={`pool-layout-sider-content-tabs-item ${activeKey === '2' ? 'active' : ''}`} onClick={() => changeTab('2')}><StarFilled /></span>
                        </div>
                    </div>
                </div>
                <div className='pool-layout-sider-list'>
                    {poolsList.map((m,index) => (
                        <div key={m.id}>
                            <div className='pool-layout-sider-list-item' onClick={() => selectPool(m, index)}>
                                <div className='pool-layout-sider-list-item-title text-10 color-2 flex items-center flex-between'>
                                    <div
                                        className='pool-layout-sider-list-item-title-left text-12'
                                        style={{ '--stock-color': colors[index % colors.length] } as CSSProperties}
                                    >
                                        <span className='pool-layout-sider-list-dot' style={{ background: colors[index % colors.length] }}></span>
                                        <span className='pool-layout-sider-list-item-title-text'>{m.name}</span>
                                    </div>
                                    <div className='pool-layout-sider-list-right flex items-center gap-4'>
                                        <span className='pool-layout-sider-list-length color-3'>{m.count}</span>
                                        <span className='pool-layout-list-icon' onClick={(e) => {
                                            e.stopPropagation()
                                            openPool(m)
                                        }}>
                                            {
                                                m.open ? <UpOutlined style={{ fontSize: 6 }} /> : <DownOutlined style={{ fontSize: 6 }} />
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {
                                m.open && (
                                    <div className='pool-layout-sider-stock-list text-12 color-2' key={m.id}>
                                        {(Array.isArray(m.stocks) ? m.stocks : []).map((s) => (
                                            <div key={s.code} className={`pool-layout-sider-stock-list-item ${activeStockCode === s.code ? 'active' : ''}`} onClick={() => selectStock(s)}>
                                                <span className='pool-layout-sider-stock-name'>{stockDisplayName(s)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )
                            }
                        </div>
                    ))}
                </div>
            </Sider>
            <Content className='pool-layout-content'>
                <div className='layout-content'>
                    {children}
                </div>
            </Content>
        </Layout>
    )
}
